
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link, useSearchParams } from 'react-router-dom';
import NocodeHeader from '@/components/nocode/NocodeHeader';
import CountdownTimer from '@/components/nocode/CountdownTimer';
import { motion } from 'framer-motion';
import GradientButton from '@/components/nocode/GradientButton';
import PaymentStatus from '@/components/nocode/PaymentStatus';
import { toast } from 'sonner';
import { verifyPayment } from '@/services/paystackService';
import { supabase } from "@/integrations/supabase/client";

interface FormData {
  name: string;
  email: string;
  phone: string;
}

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<FormData | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | 'pending'>('pending');

  useEffect(() => {
    window.scrollTo(0, 0);

    const reference = searchParams.get('reference');
    const trxref = searchParams.get('trxref');

    console.log('Payment reference:', reference);
    console.log('Payment trxref:', trxref);

    // Check if we have payment reference from Paystack
    if (reference || trxref) {
      // Get the reference to use (either should work)
      const paymentRef = reference || trxref;

      // Verify the payment with our database
      const checkPayment = async () => {
        try {
          // First try to get the payment from our database
          const { data, error } = await supabase
            .from('payments')
            .select('*')
            .eq('reference', paymentRef)
            .maybeSingle();

          if (error) {
            console.error('Error fetching payment:', error);
            setPaymentStatus('pending');
            return;
          }

          if (data) {
            // Update payment status in database
            if (data.status !== 'success') {
              await supabase
                .from('payments')
                .update({
                  status: 'success',
                  payment_date: new Date().toISOString(),
                  paystack_reference: trxref || null
                })
                .eq('id', data.id);
            }

            // Set form data from our record
            setFormData({
              name: data.name,
              email: data.email,
              phone: data.phone
            });

            setPaymentStatus('success');
            toast.success('Payment successful! Welcome to the AI-Powered No-Code Training.');
          } else {
            setPaymentStatus('pending');

            // Try to get form data from sessionStorage as fallback
            const storedData = sessionStorage.getItem('nocodeFormData');
            if (storedData) {
              setFormData(JSON.parse(storedData));
            }
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          setPaymentStatus('pending');
        }
      };

      checkPayment();
    } else if (location.state) {
      // If we have location state but no reference, assume user came from payment page
      setFormData(location.state as FormData);
      sessionStorage.setItem('nocodeFormData', JSON.stringify(location.state));
      setPaymentStatus('pending');
    } else {
      // Check sessionStorage as a fallback
      const storedData = sessionStorage.getItem('nocodeFormData');
      if (storedData) {
        setFormData(JSON.parse(storedData));
        setPaymentStatus('pending');
      } else {
        // No reference and no form data, redirect back to landing page
        navigate('/nocode');
      }
    }
  }, [location, navigate, searchParams]);

  return (
    <div className="min-h-screen w-full bg-white text-gray-800 font-poppins overflow-x-hidden">
      <NocodeHeader />

      <main className="container mx-auto px-4 py-24 md:py-32">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20
            }}
            className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <PaymentStatus status={paymentStatus} className="mb-4 justify-center" />

            <h1 className="font-montserrat font-bold text-2xl md:text-3xl mb-2 bg-clip-text text-transparent bg-nocode-gradient">
              {paymentStatus === 'success' ? '🎉 Congratulations – You\'re In!' : '🎉 Thank You for Your Interest'}
            </h1>

            <p className="text-gray-600 mb-6">
              {paymentStatus === 'success'
                ? `Welcome, ${formData?.name || 'Future Creator'}! Your registration for the AI-Powered No-Code Training has been confirmed.`
                : `We've received your information, ${formData?.name || 'Future Creator'}! Complete your payment to secure your spot.`
              }
            </p>

            <div className="bg-nocode-gray p-6 rounded-xl mb-6">
              <h3 className="font-montserrat font-semibold mb-4">
                {paymentStatus === 'success' ? 'Mark Your Calendar' : 'Limited Spots Available'}
              </h3>

              {paymentStatus === 'success' ? (
                <>
                  <p className="mb-4">Your training session starts in:</p>
                  <div className="flex justify-center mb-4">
                    <CountdownTimer hours={72} />
                  </div>
                  <p className="text-sm">
                    We've sent all the details to <span className="font-medium">{formData?.email || 'your email'}</span>.
                    Check your inbox for login instructions.
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-4">Secure your spot before time runs out:</p>
                  <div className="flex justify-center mb-4">
                    <CountdownTimer hours={24} />
                  </div>
                  <div className="mt-6">
                    <GradientButton
                      onClick={() => navigate('/nocode/payment', { state: formData })}
                      className="w-full md:w-auto py-3 px-6"
                    >
                      Complete Your Payment
                    </GradientButton>
                  </div>
                </>
              )}
            </div>

            {paymentStatus === 'success' && (
              <div className="mb-8">
                <GradientButton
                  onClick={() => window.open('https://wa.me/2348039260734', '_blank')}
                  className="w-full md:w-auto py-3 px-6"
                >
                  Join Our Private Community
                </GradientButton>
                <p className="text-sm text-gray-500 mt-2">
                  Connect with fellow participants and get exclusive updates!
                </p>
              </div>
            )}

            <div className="border-t border-gray-200 pt-6">
              <Link
                to="/nocode"
                className="text-nocode-blue hover:text-nocode-purple transition-colors duration-300"
              >
                ← Return to Homepage
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default ThankYou;
